<script setup>
import Dialog from 'primevue/dialog';
import {computed, inject, reactive, ref, watch} from "vue";
import {useBreakpoint} from "@/composables/useBreakpoint";
import {Icon} from "@iconify/vue";
import FloatLabel from "primevue/floatlabel";
import Select from "primevue/select";
import {numeric, required} from "@vuelidate/validators";
import {useVuelidate} from "@vuelidate/core";
import {useConfigStore} from "@/stores/config.js";
import {useConfigData} from "@/composables/useConfigData.js";
import {InputGroup, InputGroupAddon, InputText} from "primevue";
import {showMessage} from "@/utils/showMessage.js";
import {useCurrencyPricesStore} from "@/stores/currencyPrices.js";
import {keepNonEmptyValues} from "@/utils/helpers.js";

const emit = defineEmits(['refresh']);
const visible = inject('isShowSetPriceModal')

const props = defineProps({
  selectedRecord: Object
})

const currencyPricesStore = useCurrencyPricesStore();
const configStore = useConfigStore()
const {isXL} = useBreakpoint();
const saving = ref(false)
const form = reactive({
  "name": props.selectedRecord?.name ? props.selectedRecord.name : "",
  "price": props.selectedRecord?.price ? props.selectedRecord.price : "",
})
const rules = computed(() => {
  return {
    name: {required},
    price: {required, numeric},
  };
});
const $vulidate = useVuelidate(rules, form);

/* ---------- Select options ---------- */
const {games} = useConfigData('Currency')

const setPrice = async () => {
  $vulidate.value.$touch();
  if ($vulidate.value.$invalid) {
    return false;
  }

  /*const ok = await showAlert('You are adding new order?', 'Are you sure?', 'Yes', 'No')
  if (!ok) return*/

  const payload = keepNonEmptyValues(form)
  if (!payload) {
    showMessage('No fields are filled in.', 'warning')
    return
  }

  try {
    saving.value = true
    const res = await currencyPricesStore.action_setPrice(payload)
    if (res.result === true) {
      showMessage('New Price Set!', 'success')
      emit('refresh', true)
      visible.value = false;
    } else {
      showMessage(res, 'error')
    }
  } catch (err) {
    showMessage(err, 'error')
  } finally {
    saving.value = false
  }

}

function resetForm() {
  form.game = "";
  form.name = "";
  form.price = "";
  $vulidate.value.$reset()
}
</script>

<template>
  <Dialog v-model:visible="visible" @hide="resetForm"
          :class="['min-w-max min-h-max !border-0 !rounded-md !bg-[#0e1726]', isXL ? '' : 'w-full']"
          :position="isXL ? 'center' : 'bottom'"
          closable dismissable-mask draggable modal
          content-class="min-w-full min-h-full flex flex-col gap-4 overflow-hidden h-full w-full max-w-[calc(100vw-20px)] max-h-[calc(100vh-100px)]">
    <template #header>
      <div class="text-base py-3">
        {{ selectedRecord?.game ? "Edit Price" : "Set New Price" }}
      </div>
    </template>
    <template #closebutton="{closeCallback}">
      <div class="h-full w-[1px] bg-slate-700"></div>
      <button
          type="button"
          class="text-white-dark outline-none hover:text-red-500"
          @click="closeCallback"
      >
        <Icon icon="pajamas:close-xs"/>
      </button>
    </template>
    <div class="flex-1 h-full grid grid-cols-1 gap-4 overflow-y-auto pt-4 pb-1 text-xs 2xl:text-sm">
      <!-- ================== Name ================== -->
      <template v-if="selectedRecord?.name">
        <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm w-full">
          <FloatLabel variant="on">
            <InputText id="name" :value="selectedRecord?.name" autocomplete="off" readonly disabled
                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                       :class="[selectedRecord?.name ? 'p-filled !border-slate-800' : '!border-slate-700']"/>
            <label for="name" class="!bg-slate-900"
                   :class="[selectedRecord?.name ? '!text-slate-500' : '!text-slate-400']">Name</label>
          </FloatLabel>
        </div>
      </template>
      <template v-else>
        <div class="flex flex-col gap-1 text-xs 2xl:text-sm">
          <FloatLabel variant="on">
            <InputText id="name" v-model="form.name" autocomplete="off"
                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                       :class="{ 'has-error': $vulidate.name.$error }"/>
            <label for="name" class="!bg-slate-900"
                   :class="{ 'focus:text-red-300': $vulidate.name.$error }">Name</label>
          </FloatLabel>
          <template v-if="$vulidate.name.$error">
            <p class="text-red-300 text-[.6rem] 2xl:text-xs" v-if="!$vulidate.name.required.$response">
              This field is required
            </p>
          </template>
        </div>
      </template>
      <!-- ================== Price ================== -->
      <div class="flex flex-col gap-1 text-xs 2xl:text-sm">
        <InputGroup>
          <FloatLabel variant="on"
                      :class="{ 'has-error': $vulidate.price.$error }">
            <InputText v-allow-digits-dot id="price" v-model="form.price" autocomplete="off"
                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                       :class="{ 'has-error has-addon': $vulidate.price.$error }"/>
            <label for="price" class="!bg-slate-900"
                   :class="{ 'has-error': $vulidate.price.$error }">Price</label>
          </FloatLabel>
          <InputGroupAddon class="!bg-transparent !border-slate-700 h-full">$
          </InputGroupAddon>
        </InputGroup>
        <template v-if="$vulidate.price.$error">
          <p class="text-red-300 text-[.6rem] 2xl:text-xs"
             v-if="!$vulidate.price.required.$response">
            This field is required
          </p>
        </template>
      </div>
    </div>

    <button @click="setPrice" :disabled="saving"
            class="btn btn-outline-primary flex flex-row gap-4 py-1 w-max self-end items-center">
      <svg v-if="saving" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
           class="lucide lucide-loader-icon lucide-loader animate-spin">
        <path d="M12 2v4"/>
        <path d="m16.2 7.8 2.9-2.9"/>
        <path d="M18 12h4"/>
        <path d="m16.2 16.2 2.9 2.9"/>
        <path d="M12 18v4"/>
        <path d="m4.9 19.1 2.9-2.9"/>
        <path d="M2 12h4"/>
        <path d="m4.9 4.9 2.9 2.9"/>
      </svg>
      Save
    </button>
  </Dialog>
</template>

<style>
@import "@/assets/primevue.css";
</style>
<style>
.p-dialog-header {
  @apply flex flex-row gap-10 !px-4 !py-0 border-b border-slate-700 h-full
}

.p-dialog-header-actions {
  @apply flex flex-row items-center gap-4 h-full
}
</style>
